import { MathUtils, Vector2 } from 'three';
import { type TresContext, useRenderLoop } from '@tresjs/core';
import { useControls } from '@tresjs/leches';

interface CameraFollowOptions {
  /** Сдвиг целевой точки по Y (нормализованное значение). */
  shiftY?: number;
}

export const useTreCameraFollow = (
  context: TresContext,
  options: CameraFollowOptions = {},
) => {
  const { shiftY = 0 } = options;

  const { onLoop } = useRenderLoop();

  const { CameraFollowIsActive } = useControls('CameraFollow', {
    isActive: {
      value: true,
      type: 'boolean',
    },
    speed: {
      value: 1,
      min: 0.1,
      max: 2,
      step: 0.1,
    },
  });

  const { width, height } = useWindowSize();

  const windowHalfX = computed(() => width.value / 2);
  const windowHalfY = computed(() => height.value / 2);

  const pointerTarget = new Vector2();
  const pointerCurrent = new Vector2();

  const onMouseMove = (event: MouseEvent) => {
    if (!CameraFollowIsActive.value.value) return;

    // Normalize pointer position to make camera movement stable across resolutions.
    const normalizedX = (event.clientX - windowHalfX.value) / windowHalfX.value;
    const normalizedY = (event.clientY - windowHalfY.value) / windowHalfY.value;

    pointerTarget.x = MathUtils.clamp(normalizedX, -1, 1);
    pointerTarget.y = MathUtils.clamp(normalizedY - shiftY, -1, 1);
  };

  // Touch-параллакс для мобильных устройств
  const onTouchMove = (event: TouchEvent) => {
    if (!CameraFollowIsActive.value.value) return;

    const touch = event.touches[0];
    if (!touch) return;

    const normalizedX = (touch.clientX - windowHalfX.value) / windowHalfX.value;
    const normalizedY = (touch.clientY - windowHalfY.value) / windowHalfY.value;

    pointerTarget.x = MathUtils.clamp(normalizedX, -1, 1);
    pointerTarget.y = MathUtils.clamp(normalizedY - shiftY, -1, 1);
  };

  const onMouseLeave = () => {
    pointerTarget.set(0, 0);
  };

  document.addEventListener('mousemove', onMouseMove, { passive: true });
  document.addEventListener('touchmove', onTouchMove, { passive: true });
  document.addEventListener('mouseleave', onMouseLeave, { passive: true });

  onLoop(({ delta }) => {
    if (context?.camera.value) {
      const camera = context.camera.value;

      pointerCurrent.lerp(pointerTarget, 0.2);

      const rotationYTarget = -pointerCurrent.x * 0.09;
      const rotationXTarget = -pointerCurrent.y * 0.06;

      // Delta-based damping avoids visible jitter on unstable FPS.
      camera.rotation.x = MathUtils.damp(
        camera.rotation.x,
        rotationXTarget,
        10,
        delta,
      );
      camera.rotation.y = MathUtils.damp(
        camera.rotation.y,
        rotationYTarget,
        10,
        delta,
      );
    }
  });

  const cleanup = () => {
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('touchmove', onTouchMove);
    document.removeEventListener('mouseleave', onMouseLeave);
  };

  return cleanup;
};
